/**
 * A tela de entrada, ligada ponta a ponta a `identidade` (historia 1.3).
 *
 * **Mensagem do servidor sai verbatim.** Senha errada e e-mail inexistente recebem a mesma
 * recusa do backend — `401 nao_autenticado`, "Usuario ou senha invalidos." —, e e ela que
 * aparece. Traduzir para uma string local significaria manter o mesmo texto nos dois lados e
 * diverge-lo na primeira mudanca do servico (DW-30). Texto **nosso** aqui e so o da validacao
 * de formulario, que nasce nesta tela e nao existe do outro lado.
 *
 * **Formulario vazio nao vai a rede.** Sem e-mail ou sem senha, `POST /sessoes` responderia
 * `422` com uma mensagem de validacao de esquema — pior do que a nossa e a um round-trip de
 * distancia.
 *
 * **Senha recusada limpa o campo e devolve o foco a ele.** E o campo que a pessoa vai
 * corrigir; deixa-lo preenchido com o valor errado obriga a apagar antes de tentar de novo.
 */

import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useEntrar } from '@/modulos/identidade/sessao'
import { rotaPublica } from './publico'
import './entrar.css'

/** Texto de validacao — nosso, porque a checagem e nossa. */
const FALTA_EMAIL = 'Informe o e-mail.'
const FALTA_SENHA = 'Informe a senha.'

function Entrar() {
  const navegar = useNavigate()
  const entrarNoSistema = useEntrar()
  const campoDaSenha = useRef<HTMLInputElement>(null)

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()

    if (email.trim() === '') {
      setErro(FALTA_EMAIL)
      return
    }
    if (senha === '') {
      setErro(FALTA_SENHA)
      return
    }

    setErro(null)
    entrarNoSistema.mutate(
      { email: email.trim(), senha },
      {
        onSuccess: () => {
          void navegar({ to: '/hoje' })
        },
        onError: (falha: unknown) => {
          setErro(falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO)
          setSenha('')
          campoDaSenha.current?.focus()
        },
      },
    )
  }

  return (
    <>
      <h1 className="entrada__titulo">Entrar</h1>

      <form className="entrada__formulario" onSubmit={enviar} noValidate>
        <div className="entrada__campo">
          <label className="entrada__rotulo" htmlFor="campo-email">
            E-mail
          </label>
          <input
            id="campo-email"
            className="entrada__entrada"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(evento) => {
              setEmail(evento.target.value)
            }}
          />
        </div>

        <div className="entrada__campo">
          <label className="entrada__rotulo" htmlFor="campo-senha">
            Senha
          </label>
          <input
            id="campo-senha"
            ref={campoDaSenha}
            className="entrada__entrada"
            type="password"
            name="senha"
            autoComplete="current-password"
            value={senha}
            onChange={(evento) => {
              setSenha(evento.target.value)
            }}
          />
        </div>

        {erro === null ? null : (
          <p className="entrada__erro" role="alert">
            {erro}
          </p>
        )}

        <button className="entrada__botao" type="submit" disabled={entrarNoSistema.isPending}>
          {entrarNoSistema.isPending ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      <Link to="/esqueci-minha-senha" className="entrada__link">
        Esqueci minha senha
      </Link>
    </>
  )
}

export const rotaEntrar = createRoute({
  getParentRoute: () => rotaPublica,
  path: '/entrar',
  component: Entrar,
})
